import React, { useState, useEffect, useRef, useCallback } from 'react';
import { reviewAPI } from '../../services/api';
import socketService from '../../services/socket';
import { useAuth } from '../../context/AuthContext';

// Toast notification types
const TOAST_TYPES = {
  SUCCESS: 'success',
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error'
};

const DATE_FILTERS = {
  ALL_TIME: 'allTime',
  TODAY: 'today',
  THIS_WEEK: 'thisWeek',
  THIS_MONTH: 'thisMonth',
  LAST_3_MONTHS: 'last3Months',
  CUSTOM: 'custom'
};

function Reviews() {
  const { restaurant } = useAuth();
  const [allReviews, setAllReviews] = useState([]);
  const [filteredReviews, setFilteredReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    averageRating: 0,
    totalReviews: 0,
    distribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }
  });
  const [selectedFilter, setSelectedFilter] = useState(DATE_FILTERS.ALL_TIME);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);

  // Toast state
  const toastIdRef = useRef(0);
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = TOAST_TYPES.INFO, duration = 5000) => {
    const id = ++toastIdRef.current;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
  }, []);

  useEffect(() => {
    fetchReviews();
  }, []);

  // Socket listener for real-time review notifications
  useEffect(() => {
    if (!restaurant?._id) return;

    socketService.connect();
    socketService.joinRestaurantRoom(restaurant._id);

    // Listen for new reviews
    const unsubReviewReceived = socketService.on('review_received', (data) => {
      console.log('📝 New review received:', data);

      const rating = data.rating || data.review?.rating || 0;
      const customerName = data.customerName || data.review?.userName || 'A customer';

      addToast(
        `⭐ New ${rating}-star review from ${customerName}!`,
        rating >= 4 ? TOAST_TYPES.SUCCESS : rating >= 3 ? TOAST_TYPES.INFO : TOAST_TYPES.WARNING,
        6000
      );

      // Refresh reviews to show the new one
      fetchReviews();
    });

    return () => {
      unsubReviewReceived();
    };
  }, [restaurant?._id, addToast]);

  useEffect(() => {
    applyDateFilter();
  }, [selectedFilter, customStartDate, customEndDate, allReviews]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const response = await reviewAPI.getMyReviews({});

      const responseData = response?.data || {};
      const data = responseData.data || responseData;
      const reviewsList = data.reviews || data || [];

      const finalReviews = Array.isArray(reviewsList) ? reviewsList : [];
      setAllReviews(finalReviews);

      // Calculate stats from the reviews list
      if (finalReviews.length > 0) {
        const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
        let totalRating = 0;

        finalReviews.forEach(review => {
          distribution[review.rating] = (distribution[review.rating] || 0) + 1;
          totalRating += review.rating;
        });

        setStats({
          averageRating: (totalRating / finalReviews.length).toFixed(1),
          totalReviews: finalReviews.length,
          distribution
        });
      } else {
        setStats({
          averageRating: 0,
          totalReviews: 0,
          distribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }
        });
      }
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyDateFilter = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    let filtered = [];

    switch (selectedFilter) {
      case DATE_FILTERS.ALL_TIME:
        filtered = [...allReviews];
        break;
      case DATE_FILTERS.TODAY:
        filtered = allReviews.filter(review => {
          if (!review.createdAt) return false;
          const reviewDate = new Date(review.createdAt);
          const reviewDay = new Date(reviewDate.getFullYear(), reviewDate.getMonth(), reviewDate.getDate());
          return reviewDay.getTime() === today.getTime();
        });
        break;
      case DATE_FILTERS.THIS_WEEK:
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        filtered = allReviews.filter(review => {
          if (!review.createdAt) return false;
          const reviewDate = new Date(review.createdAt);
          return reviewDate >= weekStart;
        });
        break;
      case DATE_FILTERS.THIS_MONTH:
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        filtered = allReviews.filter(review => {
          if (!review.createdAt) return false;
          const reviewDate = new Date(review.createdAt);
          return reviewDate >= monthStart;
        });
        break;
      case DATE_FILTERS.LAST_3_MONTHS:
        const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
        filtered = allReviews.filter(review => {
          if (!review.createdAt) return false;
          const reviewDate = new Date(review.createdAt);
          return reviewDate >= threeMonthsAgo;
        });
        break;
      case DATE_FILTERS.CUSTOM:
        if (customStartDate && customEndDate) {
          const start = new Date(customStartDate);
          const end = new Date(customEndDate);
          end.setHours(23, 59, 59, 999);
          filtered = allReviews.filter(review => {
            if (!review.createdAt) return false;
            const reviewDate = new Date(review.createdAt);
            return reviewDate >= start && reviewDate <= end;
          });
        } else {
          filtered = [...allReviews];
        }
        break;
      default:
        filtered = [...allReviews];
    }

    setFilteredReviews(filtered);
  };

  const getFilterLabel = (filter) => {
    switch (filter) {
      case DATE_FILTERS.ALL_TIME:
        return 'All Time';
      case DATE_FILTERS.TODAY:
        return 'Today';
      case DATE_FILTERS.THIS_WEEK:
        return 'This Week';
      case DATE_FILTERS.THIS_MONTH:
        return 'This Month';
      case DATE_FILTERS.LAST_3_MONTHS:
        return 'Last 3 Months';
      case DATE_FILTERS.CUSTOM:
        if (customStartDate && customEndDate) {
          const start = new Date(customStartDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
          const end = new Date(customEndDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
          return `${start} - ${end}`;
        }
        return 'Custom';
      default:
        return 'All Time';
    }
  };

  const handleFilterChange = (filter) => {
    if (filter === DATE_FILTERS.CUSTOM) {
      setShowCustomDatePicker(true);
    } else {
      setSelectedFilter(filter);
      setShowCustomDatePicker(false);
    }
  };

  const handleCustomDateApply = () => {
    if (customStartDate && customEndDate) {
      setSelectedFilter(DATE_FILTERS.CUSTOM);
      setShowCustomDatePicker(false);
    }
  };

  const renderStars = (rating, size = '1.2rem') => {
    return (
      <div style={{ display: 'flex', gap: '2px' }}>
        {[1, 2, 3, 4, 5].map(star => (
          <span key={star} style={{ color: star <= rating ? '#FFB800' : '#E5E7EB', fontSize: size }}>
            ★
          </span>
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) return <div className="loading">Loading reviews...</div>;

  return (
    <div style={{ background: '#F8FAFC', minHeight: '100vh' }}>
      <div className="container">
        {/* Stats Card Header with Coral Gradient */}
        <div className="card fade-in" style={{
          marginBottom: 'var(--space-xl)',
          background: 'linear-gradient(135deg, #FF6961 0%, #E6524A 100%)',
          color: 'white',
          borderRadius: '16px',
          overflow: 'hidden'
        }}>
          <div style={{ padding: 'var(--space-xl)' }}>
            {/* Average Rating */}
            <div style={{ textAlign: 'center', marginBottom: 'var(--space-xl)' }}>
              <div style={{
                fontSize: '4rem',
                fontWeight: '800',
                color: 'white',
                marginBottom: 'var(--space-sm)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--space-sm)'
              }}>
                {stats.averageRating}
                <span style={{ fontSize: '2.5rem', color: '#FFB800' }}>★</span>
              </div>
              <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '1.1rem' }}>
                {stats.totalReviews} {stats.totalReviews === 1 ? 'Review' : 'Reviews'}
              </div>
            </div>

            {/* Rating Distribution */}
            <div>
              {[5, 4, 3, 2, 1].map(rating => {
                const count = stats.distribution[rating] || 0;
                const percentage = stats.totalReviews > 0 ? (count / stats.totalReviews) * 100 : 0;

                return (
                  <div key={rating} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-md)',
                    marginBottom: 'var(--space-md)'
                  }}>
                    <span style={{ width: '20px', fontWeight: '600', color: 'white' }}>{rating}</span>
                    <span style={{ color: '#FFB800', fontSize: '1rem' }}>★</span>
                    <div style={{
                      flex: 1,
                      height: '10px',
                      background: 'rgba(255, 255, 255, 0.3)',
                      borderRadius: '5px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${percentage}%`,
                        height: '100%',
                        background: '#FFB800',
                        borderRadius: '5px',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                    <span style={{ width: '40px', textAlign: 'right', color: 'rgba(255, 255, 255, 0.9)', fontSize: 'var(--font-size-sm)' }}>
                      {count}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Date Filter Chips */}
        <div className="fade-in" style={{
          marginBottom: 'var(--space-xl)',
          overflowX: 'auto',
          display: 'flex',
          gap: 'var(--space-md)',
          paddingBottom: 'var(--space-sm)'
        }}>
          {[
            DATE_FILTERS.ALL_TIME,
            DATE_FILTERS.TODAY,
            DATE_FILTERS.THIS_WEEK,
            DATE_FILTERS.THIS_MONTH,
            DATE_FILTERS.LAST_3_MONTHS
          ].map(filter => (
            <button
              key={filter}
              onClick={() => handleFilterChange(filter)}
              style={{
                background: selectedFilter === filter ? '#FF6961' : 'white',
                color: selectedFilter === filter ? 'white' : '#6B7280',
                padding: '10px 20px',
                borderRadius: '20px',
                border: selectedFilter === filter ? 'none' : '1px solid #E5E7EB',
                fontWeight: '600',
                fontSize: '0.95rem',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s ease',
                boxShadow: selectedFilter === filter ? '0 2px 4px rgba(255, 105, 97, 0.3)' : 'none'
              }}
            >
              {getFilterLabel(filter)}
            </button>
          ))}
          <button
            onClick={() => handleFilterChange(DATE_FILTERS.CUSTOM)}
            style={{
              background: selectedFilter === DATE_FILTERS.CUSTOM ? '#FF6961' : 'white',
              color: selectedFilter === DATE_FILTERS.CUSTOM ? 'white' : '#6B7280',
              padding: '10px 20px',
              borderRadius: '20px',
              border: selectedFilter === DATE_FILTERS.CUSTOM ? 'none' : '1px solid #E5E7EB',
              fontWeight: '600',
              fontSize: '0.95rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s ease',
              boxShadow: selectedFilter === DATE_FILTERS.CUSTOM ? '0 2px 4px rgba(255, 105, 97, 0.3)' : 'none'
            }}
          >
            📅 {getFilterLabel(DATE_FILTERS.CUSTOM)}
          </button>
        </div>

        {/* Custom Date Picker Modal */}
        {showCustomDatePicker && (
          <div className="card fade-in" style={{
            marginBottom: 'var(--space-xl)',
            padding: '20px',
            background: 'white',
            borderRadius: '12px',
            border: '1px solid #E5E7EB'
          }}>
            <h4 style={{ marginBottom: '16px', color: '#1F2937' }}>Select Date Range</h4>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.875rem', color: '#6B7280' }}>Start Date</label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  style={{
                    padding: '10px 14px',
                    borderRadius: '8px',
                    border: '1px solid #E5E7EB',
                    fontSize: '0.95rem'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.875rem', color: '#6B7280' }}>End Date</label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  style={{
                    padding: '10px 14px',
                    borderRadius: '8px',
                    border: '1px solid #E5E7EB',
                    fontSize: '0.95rem'
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                <button
                  onClick={handleCustomDateApply}
                  disabled={!customStartDate || !customEndDate}
                  style={{
                    background: '#FF6961',
                    color: 'white',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    fontWeight: '600',
                    cursor: !customStartDate || !customEndDate ? 'not-allowed' : 'pointer',
                    opacity: !customStartDate || !customEndDate ? 0.6 : 1
                  }}
                >
                  Apply
                </button>
                <button
                  onClick={() => setShowCustomDatePicker(false)}
                  style={{
                    background: '#F3F4F6',
                    color: '#6B7280',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reviews Count */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 'var(--space-lg)'
        }}>
          <h3 style={{ color: '#1F2937', fontWeight: '600', margin: 0 }}>All Reviews</h3>
          <span style={{ color: '#6B7280', fontSize: '0.95rem' }}>
            {filteredReviews.length} {filteredReviews.length === 1 ? 'review' : 'reviews'}
          </span>
        </div>

        {/* Reviews List */}
        {filteredReviews.length === 0 ? (
          <div className="card fade-in" style={{
            textAlign: 'center',
            padding: '60px 20px',
            background: 'white',
            borderRadius: '12px'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '20px', opacity: 0.5 }}>
              {selectedFilter === DATE_FILTERS.ALL_TIME ? '⭐' : '🔍'}
            </div>
            <h3 style={{ color: '#1F2937', marginBottom: '8px', fontSize: '1.3rem', fontWeight: '600' }}>
              {selectedFilter === DATE_FILTERS.ALL_TIME ? 'No Reviews Yet' : 'No Reviews Found'}
            </h3>
            <p style={{ color: '#6B7280', fontSize: '0.95rem' }}>
              {selectedFilter === DATE_FILTERS.ALL_TIME
                ? "Your restaurant hasn't received any reviews yet."
                : 'No reviews found for the selected period.'}
            </p>
            {selectedFilter !== DATE_FILTERS.ALL_TIME && (
              <button
                onClick={() => setSelectedFilter(DATE_FILTERS.ALL_TIME)}
                style={{
                  marginTop: '16px',
                  background: 'none',
                  color: '#FF6961',
                  border: 'none',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontSize: '0.95rem'
                }}
              >
                Clear Filter
              </button>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {filteredReviews.map(review => (
              <div key={review._id || review.id} className="card fade-in" style={{
                background: 'white',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
              }}>
                {/* Review Header */}
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  marginBottom: '12px'
                }}>
                  {/* Avatar */}
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: '#FFCCCB',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#FF6961',
                    fontWeight: '700',
                    fontSize: '1.2rem',
                    flexShrink: 0
                  }}>
                    {(review.userName || review.user?.name || 'U').charAt(0).toUpperCase()}
                  </div>

                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600', color: '#1F2937', fontSize: '1rem', marginBottom: '4px' }}>
                      {review.userName || review.user?.name || 'Anonymous'}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      {renderStars(review.rating)}
                      <span style={{
                        color: '#9CA3AF',
                        fontSize: '0.875rem'
                      }}>
                        {formatDate(review.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Review Content */}
                {review.comment && (
                  <p style={{
                    color: '#4B5563',
                    lineHeight: '1.6',
                    marginBottom: '12px',
                    fontSize: '0.95rem'
                  }}>
                    {review.comment}
                  </p>
                )}

                {/* Item Ratings Section */}
                {review.itemRatings && review.itemRatings.length > 0 && (
                  <div style={{
                    padding: '14px',
                    background: '#F9FAFB',
                    borderRadius: '8px',
                    marginBottom: '12px',
                    border: '1px solid #E5E7EB'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      marginBottom: '10px'
                    }}>
                      <span style={{ fontSize: '1rem' }}>🍽️</span>
                      <span style={{
                        fontWeight: '600',
                        color: '#FF6961',
                        fontSize: '0.9rem'
                      }}>
                        Item Ratings
                      </span>
                    </div>
                    {review.itemRatings.map((item, index) => (
                      <div key={index} style={{ marginBottom: index < review.itemRatings.length - 1 ? '8px' : '0' }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '12px'
                        }}>
                          <span style={{
                            color: '#374151',
                            fontSize: '0.9rem',
                            fontWeight: '500',
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {item.menuItem?.name || item.name || 'Item'}
                          </span>
                          {renderStars(item.rating, '0.9rem')}
                        </div>
                        {item.review && (
                          <p style={{
                            margin: '4px 0 0 0',
                            fontSize: '0.85rem',
                            color: '#6B7280',
                            fontStyle: 'italic'
                          }}>
                            "{item.review}"
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Order Info */}
                {review.order && (
                  <div style={{
                    padding: '10px 14px',
                    background: '#F3F4F6',
                    borderRadius: '8px',
                    marginBottom: '12px',
                    fontSize: '0.875rem',
                    color: '#6B7280'
                  }}>
                    Order #{review.order._id?.slice(-6) || 'N/A'}
                  </div>
                )}

                {/* Restaurant Reply */}
                {review.reply && (
                  <div style={{
                    marginTop: '12px',
                    padding: '14px',
                    background: '#FFF5F5',
                    borderRadius: '8px',
                    borderLeft: '3px solid #FF6961'
                  }}>
                    <div style={{
                      fontWeight: '600',
                      color: '#FF6961',
                      marginBottom: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '0.9rem'
                    }}>
                      <span>↩️</span> Your Reply
                    </div>
                    <p style={{ color: '#4B5563', margin: 0, fontSize: '0.95rem', lineHeight: '1.5' }}>
                      {review.reply}
                    </p>
                  </div>
                )}

              </div>
            ))}
          </div>
        )}
      </div>

      {/* Toast Notifications */}
      {toasts.length > 0 && (
        <div style={{
          position: 'fixed',
          top: '100px',
          right: '20px',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          maxWidth: '400px'
        }}>
          {toasts.map(toast => (
            <div
              key={toast.id}
              style={{
                padding: '16px 20px',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                animation: 'slideIn 0.3s ease',
                background: toast.type === TOAST_TYPES.SUCCESS ? '#10b981' :
                           toast.type === TOAST_TYPES.ERROR ? '#ef4444' :
                           toast.type === TOAST_TYPES.WARNING ? '#f59e0b' : '#3b82f6',
                color: 'white'
              }}
            >
              <span style={{ fontSize: '20px' }}>
                {toast.type === TOAST_TYPES.SUCCESS ? '✅' :
                 toast.type === TOAST_TYPES.ERROR ? '❌' :
                 toast.type === TOAST_TYPES.WARNING ? '⚠️' : 'ℹ️'}
              </span>
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, fontWeight: '600', fontSize: '14px' }}>
                  {toast.message}
                </p>
              </div>
              <button
                onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  cursor: 'pointer',
                  color: 'white',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}

export default Reviews;
